import repository from "@/api/Main/notifications";

const POPUP_AUTO_DISMISS_MS = 6000;
const MAX_VISIBLE_POPUPS = 5;
const NOTIFICATION_POLL_INTERVAL_MS = 15000;
const RECENT_NOTIFICATION_POPUP_WINDOW_MS = 5 * 60 * 1000;

export default {
  state: {
    notifications: [],
    unread_count: 0,
    notifications_loading: false,
    polling_interval: null,
    visibility_handler: null,
    known_notification_ids: [],
    initial_fetch_done: false,
    popup_notifications: [],
  },

  getters: {
    GET_NOTIFICATIONS: (state) => state.notifications,
    GET_UNREAD_COUNT: (state) => state.unread_count,
    GET_APPROVAL_NOTIFICATIONS: (state) => state.notifications.filter((n) => n.type === "approval"),
    GET_ANNOUNCEMENT_NOTIFICATIONS: (state) => state.notifications.filter((n) => n.type === "announcement"),
    GET_REMINDER_NOTIFICATIONS: (state) => state.notifications.filter((n) => n.type === "reminder"),
    NOTIFICATIONS_LOADING: (state) => state.notifications_loading,
    GET_POPUP_NOTIFICATIONS: (state) => state.popup_notifications,
  },

  mutations: {
    SET_NOTIFICATIONS(state, { data, unread_count, new_for_popup = [] }) {
      state.notifications = data.data || [];
      state.unread_count = unread_count || 0;
      state.known_notification_ids = state.notifications.map((n) => n.id);

      if (new_for_popup.length) {
        const existingIds = new Set(state.popup_notifications.map((n) => n.id));
        const toAdd = new_for_popup.filter((n) => !existingIds.has(n.id));
        state.popup_notifications = [...toAdd, ...state.popup_notifications].slice(0, MAX_VISIBLE_POPUPS);
      }
    },
    SET_INITIAL_FETCH_DONE(state, val) {
      state.initial_fetch_done = val;
    },
    ADD_POPUP_NOTIFICATION(state, notification) {
      if (state.popup_notifications.some((n) => n.id === notification.id)) return;
      state.popup_notifications.unshift(notification);
      if (state.popup_notifications.length > MAX_VISIBLE_POPUPS) {
        state.popup_notifications = state.popup_notifications.slice(0, MAX_VISIBLE_POPUPS);
      }
    },
    DISMISS_POPUP_NOTIFICATION(state, id) {
      state.popup_notifications = state.popup_notifications.filter((n) => n.id !== id);
    },
    CLEAR_POPUP_NOTIFICATIONS(state) {
      state.popup_notifications = [];
    },
    MARK_NOTIFICATION_READ(state, id) {
      const n = state.notifications.find((n) => n.id === id);
      if (n && !n.read_at) {
        n.read_at = new Date().toISOString();
        state.unread_count = Math.max(0, state.unread_count - 1);
      }
    },
    MARK_ALL_NOTIFICATIONS_READ(state, type) {
      state.notifications.forEach((n) => {
        if (!n.read_at && (!type || n.type === type)) {
          n.read_at = new Date().toISOString();
        }
      });
      if (type) {
        state.unread_count = state.notifications.filter((n) => !n.read_at).length;
      } else {
        state.unread_count = 0;
      }
    },
    REMOVE_NOTIFICATION(state, id) {
      const idx = state.notifications.findIndex((n) => n.id === id);
      if (idx !== -1) {
        if (!state.notifications[idx].read_at) {
          state.unread_count = Math.max(0, state.unread_count - 1);
        }
        state.notifications.splice(idx, 1);
      }
      state.popup_notifications = state.popup_notifications.filter((n) => n.id !== id);
    },
    CLEAR_ALL_NOTIFICATIONS(state, type) {
      if (type) {
        const removed = state.notifications.filter((n) => n.type === type && !n.read_at).length;
        state.notifications = state.notifications.filter((n) => n.type !== type);
        state.unread_count = Math.max(0, state.unread_count - removed);
        state.popup_notifications = state.popup_notifications.filter((n) => n.type !== type);
      } else {
        state.notifications = [];
        state.unread_count = 0;
        state.popup_notifications = [];
      }
    },
    SET_NOTIFICATIONS_LOADING(state, val) {
      state.notifications_loading = val;
    },
    SET_POLLING_INTERVAL(state, interval) {
      state.polling_interval = interval;
    },
    SET_VISIBILITY_HANDLER(state, handler) {
      state.visibility_handler = handler;
    },
  },

  actions: {
    async fetchNotifications({ commit, state, rootState }) {
      commit("SET_NOTIFICATIONS_LOADING", true);
      try {
        const res = await repository.index();
        const incoming = res.data.data?.data || res.data.data || [];
        const unreadCount = res.data.unread_count || 0;

        const prevIds = new Set(state.known_notification_ids);
        const silenced = !!rootState.Login?.user?.notifications_silenced;
        let newForPopup = [];

        if (!silenced) {
          if (state.initial_fetch_done) {
            newForPopup = incoming.filter((n) => !n.read_at && !prevIds.has(n.id));
          } else {
            const recentCutoff = Date.now() - RECENT_NOTIFICATION_POPUP_WINDOW_MS;
            newForPopup = incoming.filter((n) => {
              if (n.read_at || !n.created_at) return false;
              return new Date(n.created_at).getTime() >= recentCutoff;
            });
          }
        }

        commit("SET_NOTIFICATIONS", {
          data: { data: incoming },
          unread_count: unreadCount,
          new_for_popup: newForPopup,
        });
        commit("SET_INITIAL_FETCH_DONE", true);
      } catch (e) {
        // Silent fail — bell stays at last known state
      } finally {
        commit("SET_NOTIFICATIONS_LOADING", false);
      }
    },

    dismissPopupNotification({ commit }, id) {
      commit("DISMISS_POPUP_NOTIFICATION", id);
    },

    clearPopupNotifications({ commit }) {
      commit("CLEAR_POPUP_NOTIFICATIONS");
    },

    async markNotificationRead({ commit }, id) {
      commit("MARK_NOTIFICATION_READ", id);
      commit("DISMISS_POPUP_NOTIFICATION", id);
      try {
        await repository.markRead(id);
      } catch (e) {
        /* */
      }
    },

    async markAllNotificationsRead({ commit }, type = null) {
      commit("MARK_ALL_NOTIFICATIONS_READ", type);
      try {
        await repository.markAllRead(type ? { type } : {});
      } catch (e) {
        /* */
      }
    },

    async clearNotification({ commit }, id) {
      commit("REMOVE_NOTIFICATION", id);
      try {
        await repository.clear(id);
      } catch (e) {
        /* */
      }
    },

    async clearAllNotifications({ commit }, type = null) {
      commit("CLEAR_ALL_NOTIFICATIONS", type);
      try {
        await repository.clearAll(type ? { type } : {});
      } catch (e) {
        /* */
      }
    },

    startNotificationPolling({ dispatch, state, commit }) {
      if (state.polling_interval) return;
      dispatch("fetchNotifications");
      const interval = setInterval(() => dispatch("fetchNotifications"), NOTIFICATION_POLL_INTERVAL_MS);
      commit("SET_POLLING_INTERVAL", interval);

      if (!state.visibility_handler && typeof document !== "undefined") {
        const handler = () => {
          if (document.visibilityState === "visible") {
            dispatch("fetchNotifications");
          }
        };
        document.addEventListener("visibilitychange", handler);
        commit("SET_VISIBILITY_HANDLER", handler);
      }
    },

    stopNotificationPolling({ state, commit }) {
      if (state.polling_interval) {
        clearInterval(state.polling_interval);
        commit("SET_POLLING_INTERVAL", null);
      }
      if (state.visibility_handler && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", state.visibility_handler);
        commit("SET_VISIBILITY_HANDLER", null);
      }
      commit("SET_INITIAL_FETCH_DONE", false);
      commit("CLEAR_POPUP_NOTIFICATIONS");
    },
  },
};

export { POPUP_AUTO_DISMISS_MS, NOTIFICATION_POLL_INTERVAL_MS };
